import React, { useState, useEffect } from "react";
import { authFetch } from "../utils/authFetch";

/* =========================================================
   FULL PAGE BILLDESK REDIRECT
   ========================================================= */
const redirectToBillDesk = (redirect) => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = redirect.url; // BillDesk URL

  const addHidden = (name, value) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  };

  addHidden("bdorderid", redirect.params.bdorderid);
  addHidden("merchantid", redirect.params.mercid);
  addHidden("rdata", redirect.params.rdata);

  document.body.appendChild(form);
  form.submit();
};

function Payment2() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [duration, setDuration] = useState(null);
  const [amount, setAmount] = useState(0);
  const [loadingDuration, setLoadingDuration] = useState(true);

  // Fetch project duration on component mount
  useEffect(() => {
    const fetchDuration = async () => {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          throw new Error("Please login first");
        }

        const res = await authFetch("/api/students/me/allotted_project/duration", {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });

        if (!res.ok) {
          if (res.status === 404) {
            throw new Error("No project allotted yet");
          }
          const errorData = await res.json();
          throw new Error(errorData.detail || "Failed to fetch project duration");
        }

        const data = await res.json();
        setDuration(data.duration);
        
        // Calculate amount based on duration
        // Assuming duration is in months (e.g., "2 months", "1 month", or just number)
        let months = 1; // Default to 1 month
        
        if (typeof data.duration === 'number') {
          months = data.duration;
        } else if (typeof data.duration === 'string') {
          // Parse string like "2 months", "1 month", "6 months"
          const match = data.duration.match(/(\d+)/);
          if (match) {
            months = parseInt(match[0]);
          }
        }
        
        // Ensure months is at least 1
        months = Math.max(1, months);
        
        // Calculate amount based on months
        if(months === 1) {
          setAmount(3540);
        }
        else if(months === 2) {
          setAmount(5900);
        }
        // Add more conditions for other durations if needed
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoadingDuration(false);
      }
    };

    fetchDuration();
  }, []);

  const handlePayment = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        throw new Error("Please login first");
      }

      // Create payment order with calculated amount
      const paymentData = {
        amount: amount,
        payment_type: "OFFER" // Different payment type for Payment2
      };

      const res = await fetch("/api/payment/create-order", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}` // Send JWT token
        },
        body: JSON.stringify(paymentData),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || errorData.detail || "Payment initialization failed");
      }

      const data = await res.json();
      
      // Redirect to BillDesk
      redirectToBillDesk(data.billdesk_response.redirect);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Show loading while fetching duration
  if (loadingDuration) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.title}>Payment</h1>
          <div style={styles.loadingContainer}>
            <div style={styles.loadingSpinner}></div>
            <p>Loading project details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error if no project allotted
  if (error && error.includes("No project allotted")) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.title}>Payment</h1>
          <div style={styles.noProjectMessage}>
            <svg style={styles.errorIcon} viewBox="0 0 24 24" width="48" height="48">
              <path fill="#f39c12" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <h3>No Project Allotted</h3>
            <p>You don't have any project allotted yet. Please wait for admin confirmation.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Project Payment</h1>
        
        <div style={styles.amountSection}>
          <div style={styles.amountLabel}>Project Duration:</div>
          <div style={styles.durationValue}>{duration}</div>
          
          <div style={styles.breakdownBox}>
            <div style={styles.breakdownTotal}>
              <span>Total Amount:</span>
              <span>₹{amount.toLocaleString('en-IN')}</span>
            </div>
          </div>

          <div style={styles.amountValue}>₹{amount.toLocaleString('en-IN')}</div>
          <div style={styles.amountNote}>Project allotment fee for {duration}</div>
        </div>

      { /* <button 
          onClick={handlePayment}
          disabled={loading || !amount}
          style={loading || !amount ? styles.buttonDisabled : styles.button}
        >
          {loading ? "Processing..." : `Pay ₹${amount.toLocaleString('en-IN')}`}
        </button>*/}
        <h1> Payment window is over</h1>

        {error && !error.includes("No project allotted") && (
          <div style={styles.error}>{error}</div>
        )}

        <div style={styles.note}>
          You will be redirected to BillDesk secure payment gateway
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   STYLES
   ========================================================= */
const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#f0f2f5",
    padding: "20px",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
  },
  card: {
    background: "white",
    borderRadius: "12px",
    padding: "40px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
    maxWidth: "450px",
    width: "100%",
    textAlign: "center",
    border: "1px solid #e1e4e8"
  },
  title: {
    color: "#333",
    marginBottom: "25px",
    fontSize: "28px",
    fontWeight: "600",
    borderBottom: "2px solid #007bff",
    paddingBottom: "10px",
    display: "inline-block"
  },
  loadingContainer: {
    padding: "40px",
    textAlign: "center",
    color: "#666"
  },
  loadingSpinner: {
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #007bff",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    animation: "spin 1s linear infinite",
    margin: "0 auto 20px auto"
  },
  amountSection: {
    marginBottom: "30px",
    padding: "25px",
    backgroundColor: "#f8f9fa",
    borderRadius: "10px",
    border: "1px solid #e9ecef"
  },
  amountLabel: {
    fontSize: "16px",
    color: "#495057",
    marginBottom: "8px",
    fontWeight: "500"
  },
  durationValue: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#007bff",
    marginBottom: "20px"
  },
  breakdownBox: {
    backgroundColor: "#fff",
    padding: "15px",
    borderRadius: "8px",
    marginBottom: "20px",
    border: "1px dashed #007bff"
  },
  breakdownTotal: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "16px",
    fontWeight: "600",
    color: "#28a745"
  },
  amountValue: {
    fontSize: "42px",
    fontWeight: "700",
    color: "#28a745",
    marginBottom: "8px",
    textShadow: "1px 1px 2px rgba(0,0,0,0.1)"
  },
  amountNote: {
    fontSize: "14px",
    color: "#6c757d",
    fontStyle: "italic"
  },
  noProjectMessage: {
    padding: "40px 20px",
    textAlign: "center",
    color: "#666"
  },
  errorIcon: {
    marginBottom: "20px"
  },
  button: {
    width: "100%",
    padding: "16px",
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "18px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.3s ease",
    marginBottom: "15px",
    boxShadow: "0 4px 6px rgba(0, 123, 255, 0.2)"
  },
  buttonDisabled: {
    width: "100%",
    padding: "16px",
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "18px",
    fontWeight: "600",
    cursor: "not-allowed",
    marginBottom: "15px"
  },
  error: {
    marginTop: "15px",
    padding: "12px",
    backgroundColor: "#f8d7da",
    color: "#721c24",
    borderRadius: "6px",
    fontSize: "14px",
    border: "1px solid #f5c6cb"
  },
  note: {
    marginTop: "20px",
    fontSize: "13px",
    color: "#6c757d",
    fontStyle: "italic",
    paddingTop: "15px",
    borderTop: "1px solid #dee2e6"
  }
};

// Add animation keyframes
const styleSheet = document.styleSheets[0];
if (styleSheet) {
  try {
    styleSheet.insertRule(`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `, styleSheet.cssRules.length);
  } catch (e) {
    console.log("Could not insert CSS rule:", e);
  }
}

export default Payment2;
